/**
 * Danger Zone Card Component
 * Delete account and logout functionality
 */

import { useState } from 'react';
import { AlertTriangle, Trash2, LogOut, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAuthStore } from '../../../../stores/auth-store';
import { api } from '../../../../services/api';

export function DangerZoneCard() {
  const { t } = useLanguage();
  const { logout } = useAuthStore();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError(t('settings.danger.typeDeleteError', 'Please type DELETE to confirm'));
      return;
    }

    setDeleteError('');
    setIsDeleting(true);

    try {
      await api.deleteAccount();
      logout();
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('settings.danger.deleteFailed', 'Failed to delete account'));
      setIsDeleting(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <div className="bg-white border border-red-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-red-100 bg-red-50/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="font-semibold text-red-700">
              {t('settings.danger.title', 'Danger Zone')}
            </h2>
            <p className="text-sm text-red-600/80">
              {t('settings.danger.subtitle', 'Irreversible account actions')}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Delete account section */}
        <div>
          <p className="text-sm text-gray-600 mb-4">
            {t('settings.danger.warning', 'Once you delete your account, there is no going back. Please be certain.')}
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {t('settings.danger.deleteAccount', 'Delete Account')}
            </button>
          ) : (
            <div className="space-y-4 max-w-md p-4 bg-red-50 border border-red-200 rounded-xl">
              {deleteError && (
                <div className="flex items-center gap-2 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {deleteError}
                </div>
              )}

              <p className="text-sm text-gray-700 font-medium">
                {t('settings.danger.permanentDelete', 'This will permanently delete your account and all associated data including:')}
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>{t('settings.danger.dataList.watchlist', 'Your watchlist and tracked products')}</li>
                <li>{t('settings.danger.dataList.checklists', 'Your checklists and shopping lists')}</li>
                <li>{t('settings.danger.dataList.notifications', 'Your notification preferences')}</li>
                <li>{t('settings.danger.dataList.subscription', 'Your subscription (if applicable)')}</li>
              </ul>

              <div>
                <label htmlFor="delete-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.danger.typeDelete', 'Type')}{' '}
                  <span className="font-mono bg-gray-100 px-1 rounded">DELETE</span>{' '}
                  {t('settings.danger.toConfirm', 'to confirm')}
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="DELETE"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                    setDeleteError('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isDeleting
                    ? t('settings.danger.deleting', 'Deleting...')
                    : t('settings.danger.permanentDeleteButton', 'Permanently Delete Account')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Logout section */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {t('settings.logout', 'Log out of all devices')}
          </button>
        </div>
      </div>
    </div>
  );
}
